import React, { useState, useRef, useEffect } from 'react';
import { HiMenu } from 'react-icons/hi';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useAuthStore } from '../../store/authStore';
import Sidebar from '../Sidebar/Sidebar';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuthStore();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setProfileDropdownOpen(false);
      navigate(ROUTES.LOGIN, { replace: true });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // Get page title based on path
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/courses')) return '강의 관리';
    if (path.includes('/products')) return '상품 관리';
    if (path.includes('/instructors')) return '강사 관리';
    // order-sheets는 결제 관리의 하위 화면이므로 결제 관리로 표시
    if (path.includes('/payments') || path.includes('/order-sheets')) return '결제 관리';
    if (path.includes('/enrollments')) return '수강권 관리';
    if (path.includes('/institutions')) return '기관 관리';
    if (path.includes('/deliveries')) return '배송 관리';
    if (path.includes('/users')) return '사용자 관리';
    if (path.includes('/admins')) return '관리자 관리';
    if (path.includes('/settings')) return '설정';
    return '대시보드';
  };

  const MenuIcon = HiMenu as React.ComponentType<{ size?: number }>;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />

      <div className="ml-0 flex flex-1 flex-col overflow-hidden transition-all duration-300 md:ml-[280px]">
        <header className="sticky top-0 z-40 flex flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              className="-ml-2 rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 md:hidden"
              onClick={toggleSidebar}>
              <MenuIcon size={24} />
            </button>
            <h1 className="text-xl font-bold tracking-tight text-slate-800">{getPageTitle()}</h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            {/* <button className="p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-blue-600 transition-colors relative">
              <HiBell size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button> */}

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center gap-3 rounded-full border border-transparent py-1 pr-1 pl-2 transition-all hover:border-slate-200 hover:bg-slate-100">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 text-sm font-medium text-white shadow-md">
                  {user?.name?.[0]?.toUpperCase() || 'A'}
                </div>
                <span className="hidden max-w-[120px] truncate text-sm font-medium text-slate-700 md:block">
                  {user?.name || 'Admin'}
                </span>
                <svg
                  className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {profileDropdownOpen && (
                <div className="shadow-glass animate-scale-in absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl border border-slate-100 bg-white py-2">
                  <div className="border-b border-slate-50 px-4 py-3">
                    <p className="text-sm font-medium text-slate-900">관리자</p>
                    <p className="truncate text-xs text-slate-500">{user?.email}</p>
                  </div>

                  <div className="py-1">
                    <button
                      onClick={() => {
                        setProfileDropdownOpen(false);
                        navigate(ROUTES.DASHBOARD_PROFILE);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50">
                      <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      프로필 설정
                    </button>
                  </div>

                  <div className="border-t border-slate-50 py-1">
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      로그아웃
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="relative flex flex-1 flex-col overflow-hidden bg-slate-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
